/**
 * Client-side AI tool calls: the server asks the app to open the upload
 * picker, record/transcribe audio, navigate or show a notice, and the app
 * sends the results back (`submitToolResults`). Also owns the "upload a
 * transcript?" prompt the open_file_upload tool shows.
 */
import { useCallback, useState } from 'react';

import { showDialog } from '@/utils/dialogs';
import { aiAPI } from '../../services/api';
import type { ChatMessage, ToolCall, ToolResult, UnifiedAIResponse } from '../../types';
import { trialAnalysisUsedMessage } from '../../utils/trialAnalysisUsed';
import { STREAMING_BUBBLE_ID, WAITING_BUBBLE_ID } from './chatConstants';
import { chatSendErrorMessage } from './chatErrors';
import type {
  ChatRouter,
  LoadMessages,
  LoadSessions,
  Loader,
  SetMessages,
  SetState,
  TaskCompletion,
  TFn,
  TrialState,
} from './chatFlowTypes';
import type { ConversationGuard } from './conversationGuard';
import type { ChatOperationRefs } from './useChatOperationRefs';

export function useChatToolCalls({
  ops,
  guard,
  currentSessionId,
  language,
  t,
  waitingTipKey,
  router,
  setMessages,
  setSendingMessage,
  setActiveTaskId,
  loadMessages,
  loadSessions,
  loadQuotaStatus,
  loadRecentDocuments,
  trialStatus,
  refreshTrialStatus,
  waitForTaskResult,
}: {
  ops: ChatOperationRefs;
  guard: ConversationGuard;
  currentSessionId: number | null;
  language: string;
  t: TFn;
  waitingTipKey: string;
  router: ChatRouter;
  setMessages: SetMessages;
  setSendingMessage: SetState<boolean>;
  setActiveTaskId: SetState<string | null>;
  loadMessages: LoadMessages;
  loadSessions: LoadSessions;
  loadQuotaStatus: Loader;
  loadRecentDocuments: Loader;
  trialStatus: TrialState['trialStatus'];
  refreshTrialStatus: TrialState['refreshTrialStatus'];
  waitForTaskResult: TaskCompletion['waitForTaskResult'];
}) {
  const {
    activeOperationSessionRef,
    handleToolCallsRef,
    isToolSubmitInProgressRef,
    optimisticMessagesRef,
    pendingScrollToBottomRef,
    pendingToolCallsRef,
    pendingToolIntentRef,
    pendingToolResultsRef,
    pendingUploadToolRef,
    pendingVoiceToolParamsRef,
    pendingVoiceToolRef,
    pickAudioForTranscriptionRef,
    shouldAutoScrollRef,
    suppressBubbleRef,
    taskWaiterRef,
  } = ops;

  const [showUploadPromptModal, setShowUploadPromptModal] = useState(false);
  const [uploadPromptMessage, setUploadPromptMessage] = useState('');

  const submitToolResults = useCallback(async (
    toolResults: ToolResult[],
    options?: {
      originIntent?: string | null;
      documentId?: number;
      analysisInstructions?: string;
      includeInICFProgress?: boolean;
    }
  ) => {
    if (!toolResults || toolResults.length === 0) return;
    if (isToolSubmitInProgressRef.current) {
      console.log('🚫 Tool result submission already in progress, skipping duplicate call');
      return;
    }
    isToolSubmitInProgressRef.current = true;

    const originIntent = options?.originIntent ?? pendingToolIntentRef.current ?? undefined;
    const forceIntent =
      originIntent && ['chat', 'analyze', 'hybrid', 'clarify'].includes(originIntent)
        ? (originIntent as 'chat' | 'analyze' | 'hybrid' | 'clarify')
        : undefined;
    const sessionId = currentSessionId || activeOperationSessionRef.current || undefined;
    if (sessionId) {
      activeOperationSessionRef.current = sessionId;
    }

    setSendingMessage(true);
    setMessages((prev) => {
      const filtered = prev.filter((m) => m.id !== WAITING_BUBBLE_ID);
      return [
        ...filtered,
        {
          id: WAITING_BUBBLE_ID,
          role: 'assistant',
          content: t(waitingTipKey),
          created_at: new Date().toISOString(),
        } as ChatMessage,
      ];
    });

    let pendingTaskId: string | null = null;
    try {
      const asyncResponse = await aiAPI.sendMessage({
        sessionId,
        toolResults,
        forceIntent,
        documentId: options?.documentId,
        analysisInstructions: options?.analysisInstructions,
        includeInICFProgress: options?.includeInICFProgress,
        language,
      });

      if (asyncResponse.status === 'CLARIFICATION_NEEDED') {
        const prompt = asyncResponse.clarification_prompt || t('chat.uploadTranscriptPromptMessage');
        setMessages((prev) => [
          ...prev.filter((m) => m.id !== WAITING_BUBBLE_ID),
          {
            id: Date.now() + 1,
            role: 'assistant',
            content: prompt,
            created_at: new Date().toISOString(),
          } as ChatMessage,
        ]);
        shouldAutoScrollRef.current = true;
        pendingScrollToBottomRef.current = true;
        return;
      }

      if (asyncResponse.status === 'TOOL_REQUIRED' && asyncResponse.pending_tool_calls?.length) {
        setMessages((prev) => prev.filter((m) => m.id !== WAITING_BUBBLE_ID));
        handleToolCallsRef.current(asyncResponse.pending_tool_calls, asyncResponse.detected_intent);
        return;
      }

      if (asyncResponse.status === 'WAITING_FOR_CLIENT_TOOL' && asyncResponse.pending_tool_calls?.length) {
        setMessages((prev) => prev.filter((m) => m.id !== WAITING_BUBBLE_ID));
        handleToolCallsRef.current(asyncResponse.pending_tool_calls, asyncResponse.detected_intent);
        return;
      }

      // Handle sync-completed responses (e.g. marker followup from stored data)
      if (asyncResponse.status === 'COMPLETE_SYNC') {
        setMessages((prev) => prev.filter((m) => m.id !== WAITING_BUBBLE_ID));
        const sid = asyncResponse.session_id ?? currentSessionId;
        if (sid) {
          setSendingMessage(false);
          suppressBubbleRef.current = true;
          optimisticMessagesRef.current.delete(sid);
          await loadMessages(sid);
          suppressBubbleRef.current = false;
          await loadSessions();
          await loadQuotaStatus();
        }
        return;
      }

      if (!asyncResponse.task_id) {
        throw new Error('No task id returned');
      }

      if (trialStatus?.active) {
        await refreshTrialStatus();
      }

      pendingTaskId = asyncResponse.task_id;
      taskWaiterRef.current.add(asyncResponse.task_id);
      setActiveTaskId(asyncResponse.task_id);
      const completionResult = await waitForTaskResult<UnifiedAIResponse>(asyncResponse.task_id);
      setActiveTaskId(null);

      if (completionResult?.status === 'WAITING_FOR_CLIENT_TOOL' && completionResult.pending_tool_calls?.length) {
        setMessages((prev) => prev.filter((m) => m.id !== WAITING_BUBBLE_ID));
        handleToolCallsRef.current(completionResult.pending_tool_calls, completionResult.detected_intent);
        return;
      }

      const sid = asyncResponse.session_id ?? sessionId;
      if (sid) {
        await loadMessages(sid);
        await loadSessions();
        await Promise.all([loadQuotaStatus(), loadRecentDocuments()]);
      }
    } catch (error: any) {
      if (guard.isViewing(sessionId)) {
        setMessages((prev) => prev.filter((m) => m.id !== WAITING_BUBBLE_ID && m.id !== STREAMING_BUBBLE_ID));
      }
      showDialog(trialAnalysisUsedMessage(t, language, error) || chatSendErrorMessage(t, error));
    } finally {
      if (pendingTaskId) {
        taskWaiterRef.current.delete(pendingTaskId);
      }
      setActiveTaskId(null);
      setSendingMessage(false);
      activeOperationSessionRef.current = null;
      isToolSubmitInProgressRef.current = false;
      pendingToolResultsRef.current = [];
      pendingToolIntentRef.current = null;
    }
  }, [
    currentSessionId, guard, language, loadMessages, loadQuotaStatus, loadSessions, t, waitForTaskResult, waitingTipKey,
    // Stable refs and state setters (identity never changes):
    activeOperationSessionRef, handleToolCallsRef, isToolSubmitInProgressRef, optimisticMessagesRef,
    pendingScrollToBottomRef, pendingToolIntentRef, pendingToolResultsRef, shouldAutoScrollRef,
    suppressBubbleRef, taskWaiterRef, setActiveTaskId, setMessages, setSendingMessage,
  ]);

  const handleToolCalls = useCallback((toolCalls: ToolCall[], originIntent?: string) => {
    if (!toolCalls || toolCalls.length === 0) return;
    pendingToolCallsRef.current = toolCalls;
    pendingToolIntentRef.current = originIntent || null;
    pendingToolResultsRef.current = [];

    const immediateResults: ToolResult[] = [];

    for (const call of toolCalls) {
      switch (call.tool_name) {
        case 'open_file_upload': {
          pendingUploadToolRef.current = call;
          if (immediateResults.length > 0) {
            pendingToolResultsRef.current = immediateResults;
          }
          const promptText = (call.parameters as any)?.prompt_text;
          setUploadPromptMessage(
            typeof promptText === 'string' && promptText.trim().length > 0
              ? promptText
              : t('chat.uploadTranscriptPromptMessage')
          );
          setShowUploadPromptModal(true);
          return;
        }
        case 'start_voice_recording': {
          pendingVoiceToolRef.current = call;
          pendingVoiceToolParamsRef.current = call.parameters as Record<string, any>;
          if (immediateResults.length > 0) {
            pendingToolResultsRef.current = immediateResults;
          }
          const mode = (call.parameters as any)?.mode;
          if (mode === 'realtime') {
            const msg = t('transcription.realtimeNotAvailable');
            showDialog(msg);
          }
          pickAudioForTranscriptionRef.current();
          return;
        }
        case 'transcribe_audio': {
          pendingVoiceToolRef.current = call;
          pendingVoiceToolParamsRef.current = call.parameters as Record<string, any>;
          if (immediateResults.length > 0) {
            pendingToolResultsRef.current = immediateResults;
          }
          const streaming = (call.parameters as any)?.streaming;
          if (streaming) {
            const msg = t('transcription.realtimeNotAvailable');
            showDialog(msg);
          }
          pickAudioForTranscriptionRef.current();
          return;
        }
        case 'join_meeting': {
          const msg = t('transcription.meetingBotNotAvailable');
          showDialog(msg);
          immediateResults.push({
            tool_call_id: call.tool_call_id,
            tool_name: call.tool_name,
            status: 'cancelled',
            result: { reason: 'not_available' },
          });
          break;
        }
        case 'show_notification': {
          const message = (call.parameters as any)?.message || t('common.info');
          showDialog(message);
          immediateResults.push({
            tool_call_id: call.tool_call_id,
            tool_name: call.tool_name,
            status: 'success',
            result: { shown: true },
          });
          break;
        }
        case 'navigate_to': {
          const screen = (call.parameters as any)?.screen;
          if (screen) {
            try {
              router.push(screen);
            } catch {
              // ignore navigation errors
            }
          }
          immediateResults.push({
            tool_call_id: call.tool_call_id,
            tool_name: call.tool_name,
            status: 'success',
            result: { navigated: true, screen },
          });
          break;
        }
        case 'trigger_action': {
          const action = (call.parameters as any)?.action;
          immediateResults.push({
            tool_call_id: call.tool_call_id,
            tool_name: call.tool_name,
            status: 'success',
            result: { action, handled: true },
          });
          break;
        }
        default:
          break;
      }
    }

    if (immediateResults.length > 0) {
      submitToolResults(immediateResults, { originIntent });
    }
  }, [
    router, t, submitToolResults,
    pendingToolCallsRef, pendingToolIntentRef, pendingToolResultsRef, pendingUploadToolRef,
    pendingVoiceToolParamsRef, pendingVoiceToolRef, pickAudioForTranscriptionRef,
  ]);

  handleToolCallsRef.current = handleToolCalls;

  const handleUploadPromptCancel = useCallback(() => {
    setShowUploadPromptModal(false);
    const pendingTool = pendingUploadToolRef.current;
    if (!pendingTool) return;

    const originIntent = pendingToolIntentRef.current;
    const extraResults = pendingToolResultsRef.current || [];

    pendingUploadToolRef.current = null;
    pendingToolResultsRef.current = [];

    const cancelledResult: ToolResult = {
      tool_call_id: pendingTool.tool_call_id,
      tool_name: pendingTool.tool_name,
      status: 'cancelled',
      result: { reason: 'user_cancelled' },
    };

    void submitToolResults([...extraResults, cancelledResult], { originIntent });
  }, [submitToolResults, pendingToolIntentRef, pendingToolResultsRef, pendingUploadToolRef]);

  /** The user closed the analysis file picker an open_file_upload call opened. */
  const reportDocumentPickCancelled = async () => {
    if (pendingUploadToolRef.current) {
      const extraResults = pendingToolResultsRef.current || [];
      const cancelledResult: ToolResult = {
        tool_call_id: pendingUploadToolRef.current.tool_call_id,
        tool_name: pendingUploadToolRef.current.tool_name,
        status: 'cancelled',
        result: { reason: 'user_cancelled' },
      };
      pendingUploadToolRef.current = null;
      pendingToolResultsRef.current = [];
      await submitToolResults([...extraResults, cancelledResult], {
        originIntent: pendingToolIntentRef.current,
      });
    }
  };

  /** The user closed the audio picker a voice tool call opened. */
  const reportAudioPickCancelled = useCallback(async () => {
    if (!pendingVoiceToolRef.current) return;
    const extraResults = pendingToolResultsRef.current || [];
    const cancelledResult: ToolResult = {
      tool_call_id: pendingVoiceToolRef.current.tool_call_id,
      tool_name: pendingVoiceToolRef.current.tool_name,
      status: 'cancelled',
      result: { reason: 'user_cancelled' },
    };
    pendingVoiceToolRef.current = null;
    pendingVoiceToolParamsRef.current = null;
    pendingToolResultsRef.current = [];
    await submitToolResults([...extraResults, cancelledResult], {
      originIntent: pendingToolIntentRef.current,
    });
  }, [submitToolResults, pendingToolIntentRef, pendingToolResultsRef, pendingVoiceToolParamsRef, pendingVoiceToolRef]);

  return {
    showUploadPromptModal,
    setShowUploadPromptModal,
    uploadPromptMessage,
    submitToolResults,
    handleToolCalls,
    handleUploadPromptCancel,
    reportDocumentPickCancelled,
    reportAudioPickCancelled,
  };
}
